import { Injectable, Signal, WritableSignal, computed, inject, signal } from "@angular/core";
import { takeUntilDestroyed, toObservable } from "@angular/core/rxjs-interop";
import { Observable, map, combineLatest, filter, switchMap, catchError, of, debounceTime, shareReplay, distinctUntilChanged, tap } from "rxjs";
import { IMapUpdate, MapMode, MapStateService } from "../map/map-state.service";
import { environment } from "src/environments/environment";
import { HttpDataService, IResponseWrapper } from "../httpdata.service";
import { MapNewDwellingResponse, INewDwellingsFilter, INewDwellingsMapRequest, IProjectStageDto } from "src/models/new-dwelling/new-dwelling.model";
import { NewDwellingSalesStateService } from "./new-dwelling-sales-state.service";
import { ILatLng } from "polydraw";
import { TemporaryArea } from "src/app/models/map/market-filter.model";
import { PolygonUtil } from "src/util/map/polygon.util";
import { AreaBuilderDataService } from "../area-builder-data.service";
import { MapActionsService } from "../map/map-actions.service";
import { MapMarkerType } from "src/models/map/map-marker-type.enum";

@Injectable()
export class NewDwellingSalesDataService {
    private readonly stateService = inject(NewDwellingSalesStateService);
    private readonly mapStateService = inject(MapStateService);
    private readonly areaBuilderService = inject(AreaBuilderDataService);
    private readonly http = inject(HttpDataService);
    private readonly mapActionsService = inject(MapActionsService);
    private readonly newDwellingsInFilteredAreaUrl = `${environment.apiUrl}/Map/NewDwellingsInFilteredArea`;

    private readonly _selectedStage: WritableSignal<IProjectStageDto> = signal(undefined);
    readonly activeStage: Signal<IProjectStageDto> = this._selectedStage.asReadonly();

    private readonly _selectedStageEstateTypeId: WritableSignal<number> = signal(null);
    readonly activeStageEstateTypeId: Signal<number> = this._selectedStageEstateTypeId.asReadonly();

    setSelectedStage(stage: IProjectStageDto | null, stageEstateTypeId: number = null): void {
        const selectedStage = stage ?? undefined;
        this._selectedStageEstateTypeId.set(selectedStage ? stageEstateTypeId : null);
        this._selectedStage.set(selectedStage);

        if (selectedStage) {
            this.mapActionsService.addCurrentMarkerAt({ lat: selectedStage.latitude, lng: selectedStage.longitude });
        } else {
            this.mapActionsService.clearMarkerGroupLayer(MapMarkerType.SelectedSale);
            this.mapActionsService.clearMarkerGroupLayer(MapMarkerType.Outline);
        }
    }

    private readonly filterValue$ = toObservable(this.stateService.filterFormAPI);

    private readonly _mapDataIsLoading: WritableSignal<boolean> = signal(false);
    readonly mapDataIsLoading: Signal<boolean> = this._mapDataIsLoading.asReadonly();

    sales$: Observable<MapNewDwellingResponse> = combineLatest([
        this.mapStateService.mapUpdateBoundsOrPolygons$
        , this.filterValue$
        , this.mapStateService.mapMode$]).pipe(
            debounceTime(300),
            filter(([mapState, filter, mapMode]) => mapState.state.bounds !== null && mapMode === MapMode.NewDwellings),
            distinctUntilChanged((prev, curr) => this.isSameSalesQuery(prev, curr)),
            tap(() => this._mapDataIsLoading.set(true)),
            switchMap(([mapState, filter]) => this.getStages(filter)),
            map((res) => res?.projects != null ? res : { projects: [], statistics: [] }),
            tap(() => this._mapDataIsLoading.set(false)),
            takeUntilDestroyed(),
            shareReplay(1)
        );



    private isSameSalesQuery(
        prev: [IMapUpdate, INewDwellingsFilter, MapMode],
        curr: [IMapUpdate, INewDwellingsFilter, MapMode]
    ): boolean {
        const [prevMapState, prevFilter, prevMode] = prev;
        const [currMapState, currFilter, currMode] = curr;
        const sameFilter = JSON.stringify(prevFilter) === JSON.stringify(currFilter);

        if (prevMode !== currMode) {
            return false;
        }

        if (!sameFilter) {
            return false;
        }

        const prevPolygons = prevMapState.polygons;
        const currPolygons = currMapState.polygons;
        const hasPrevPolygon = (prevPolygons?.length ?? 0) > 0;
        const hasCurrPolygon = (currPolygons?.length ?? 0) > 0;

        // When polygons are used, compare polygons instead of bounds.
        if (hasPrevPolygon || hasCurrPolygon) {
            return hasPrevPolygon === hasCurrPolygon && JSON.stringify(prevPolygons) === JSON.stringify(currPolygons);
        }

        const prevBounds = prevMapState.state.bounds;
        const currBounds = currMapState.state.bounds;

        if (prevBounds == null || currBounds == null) {
            return prevBounds === currBounds;
        }

        const prevNorthEast = prevBounds.getNorthEast();
        const prevSouthWest = prevBounds.getSouthWest();
        const currNorthEast = currBounds.getNorthEast();
        const currSouthWest = currBounds.getSouthWest();

        const sameBounds =
            prevNorthEast.lat === currNorthEast.lat &&
            prevNorthEast.lng === currNorthEast.lng &&
            prevSouthWest.lat === currSouthWest.lat &&
            prevSouthWest.lng === currSouthWest.lng;

        return sameBounds;
    }

    private getStages(filter: INewDwellingsFilter): Observable<MapNewDwellingResponse> {

        const geoBorder = this.selectedPolygon();
        const areas = this.selectedAreas();

        const request: INewDwellingsMapRequest = {
            geoBorder: geoBorder,
            areas: areas,
            estateTypes: filter.estateTypes,
            ownershipTypes: filter.ownershipTypes,
            projectDevelopmentStateTypes: filter.buildStatus,
            saleStateTypes: filter.saleStatus,
            saleStartFromToDate: {
                useFromToDate: filter.useFromToDate,
                fromDate: filter.fromDate,
                toDate: filter.toDate,
                numberOfMonthsToShow: filter.numberOfMonthsToShow
            }
        };

        const requestUrl = this.newDwellingsInFilteredAreaUrl;

        let observable = this.http.post<IResponseWrapper<MapNewDwellingResponse>>(requestUrl, request).pipe(
            catchError(() => {
                return of({ data: null });
            }),
            map((res) => res.data)
        );
        return observable;
    }

    private isSelfDrawn: Signal<boolean> = computed(() => {
        const mapData = this.areaBuilderService.getAreasAndPolygons();
        return mapData.selfDrawnPolygons?.length > 0;
    });

    private selectedPolygon: Signal<ILatLng[][][]> = computed(() => {
        let polygon: ILatLng[][][] = null;
        const mapData = this.areaBuilderService.getAreasAndPolygons();
        if (
            mapData.areas?.length === 0 &&
            !this.isSelfDrawn() &&
            this.mapStateService.mapUpdateBoundsOrPolygonSignal().state.getMapBoundParams()
        ) {
            polygon = PolygonUtil.createPolygonFromBounds(this.mapStateService.mapUpdateBoundsOrPolygonSignal().state.getMapBoundParams());
        } else {
            polygon = mapData.selfDrawnPolygons;
        }
        return polygon;
    });

    private selectedAreas: Signal<TemporaryArea[]> = computed(() => {
        let areas: TemporaryArea[] = [];
        const mapData = this.areaBuilderService.getAreasAndPolygons();
        if (mapData.areas?.length != 0) {
            areas = mapData.areas;
        }

        return areas;
    });
}
